import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  loadRecaptchaEnterprise,
  RecaptchaEnterpriseApi
} from '@/infra/recaptcha/recaptcha-enterprise-loader'
import errorMonitor from '@/infra/tracking/error-monitor'
import { settings } from '@/main/config/settings'
import { setRecaptchaSiteKey, setRecaptchaToken } from './recaptcha-token-store'

export type RecaptchaCallback = () => Promise<unknown>
export type RecaptchaErrorReason = 'not_ready' | 'token_failed' | 'timeout'
export type RecaptchaAction = string

export interface RecaptchaContextType {
  executeRecaptcha: (
    callback: RecaptchaCallback,
    action: RecaptchaAction,
    onError?: (reason: RecaptchaErrorReason) => void
  ) => Promise<void>
}

export const RecaptchaContext = createContext<RecaptchaContextType | undefined>(undefined)

// Ações protegidas por reCAPTCHA
export const RECAPTCHA_DISPATCH_ACTION = {
  CHECK_DOMAIN: 'login_check_domain',
  AUTHENTICATE_PASSWORD: 'login_authenticate_password',
  VERIFY_CODE: 'login_verify_code',
  RESEND_CODE: 'login_resend_code',
  FORGOT_PASSWORD: 'forgot_password_submit',
  RESEND_ACCESS: 'resend_access_submit',
  RESET_PASSWORD: 'reset_password_submit',
  CB_UNICO_CREATE_PROCESS: 'cb_unico_create_process',
  USER_CHANGE_PASSWORD_SUBMIT: 'user_change_password_submit'
} as const

const EXECUTION_TIMEOUT_MS = 10_000
const MAX_ATTEMPTS = 3
const RETRY_DELAY_MS = 500
const TIMEOUT_ERROR_MESSAGE = '[reCAPTCHA] Token execution timed out'

interface RecaptchaProviderProps {
  children: ReactNode
}

// Bridge entre Google reCAPTCHA e app
// Gera token, executa callback e distingue erros (reCAPTCHA vs negócio)
//
// O script é obtido pelo loader compartilhado (`loadRecaptchaEnterprise`), o mesmo que
// a telemetria horizontal do Account Defender usa, e nunca é derrubado no unmount.

export const RecaptchaProvider = ({ children }: RecaptchaProviderProps) => {
  const [api, setApi] = useState<RecaptchaEnterpriseApi | null>(null)

  // Evita múltiplas requisições simultâneas
  const isExecutingRef = useRef<boolean>(false)

  useEffect(() => {
    // Desligado por ambiente: não faz sentido baixar o script se nenhum token será gerado.
    // Sem chave configurada (dev/preview) o loader rejeitaria em toda montagem e
    // encheria o errorMonitor de ruído. O efeito para o chamador é o mesmo: `api` fica
    // null e `executeRecaptcha` responde 'not_ready'. Espelha o guard da telemetria.
    if (!settings.RECAPTCHA_ENABLED || !settings.RECAPTCHA_SITE_KEY_V3) {
      return
    }

    let active = true

    // Independente do componente de telemetria: uma rota protegida precisa funcionar
    // mesmo que a telemetria esteja desabilitada ou tenha falhado.
    loadRecaptchaEnterprise(settings.RECAPTCHA_SITE_KEY_V3)
      .then(loaded => {
        if (active) {
          setApi(loaded)
        }
      })
      .catch((error: unknown) => {
        // Sem API o executeRecaptcha responde 'not_ready' ao chamador; aqui só registramos.
        console.error('[reCAPTCHA] Failed to load script', error)
        errorMonitor.captureException(error, {
          tags: { feature: 'recaptcha' },
          extra: { reason: 'script_load_failed' },
          level: 'warning'
        })
      })

    return () => {
      active = false
    }
  }, [])

  const executeRecaptcha = useCallback(
    async (
      callback: RecaptchaCallback,
      action: RecaptchaAction,
      onError?: (reason: RecaptchaErrorReason) => void
    ) => {
      // Bloqueia execução simultânea
      if (isExecutingRef.current) return
      isExecutingRef.current = true

      // Kill switch de ambiente: o submit segue SEM token.
      //
      // Precisa ser bypass, não bloqueio: cair no `!api` abaixo responderia 'not_ready' e
      // travaria o login — o oposto do que desligar a verificação deve fazer. Erros daqui
      // são de negócio e propagam para o chamador, como na FASE 2 do fluxo normal.
      if (!settings.RECAPTCHA_ENABLED) {
        try {
          await callback()
        } finally {
          isExecutingRef.current = false
        }
        return
      }

      // Flag crucial: distingue erro na FASE 1 (token) vs FASE 2 (callback)
      let tokenObtained = false

      try {
        // FASE 1: Garantir o runtime pronto antes de gerar o token.
        //
        // Em rotas que disparam a ação no MOUNT (ex.: /cbu/start, que auto-inicia a
        // criação do processo), o `api` (state) ainda pode ser null: o efeito de load
        // do provider é assíncrono e, pela ordem do React, roda depois dos efeitos dos
        // filhos. Em vez de falhar com 'not_ready', aguardamos a promessa (memoizada)
        // do loader — o mesmo runtime que o efeito acabará usando. Só é 'not_ready' se
        // o script realmente falhar/expirar (o loader tem timeout próprio). No login
        // nada muda: lá a ação parte de um clique, quando o `api` já está pronto.
        let recaptchaApi = api
        if (!recaptchaApi) {
          // Espelha o guard do efeito de load: sem chave não há script a carregar.
          if (!settings.RECAPTCHA_SITE_KEY_V3) {
            console.error('[reCAPTCHA] Not ready: site key ausente')
            onError?.('not_ready')
            return
          }
          try {
            recaptchaApi = await loadRecaptchaEnterprise(settings.RECAPTCHA_SITE_KEY_V3)
            setApi(recaptchaApi)
          } catch (loadError) {
            console.error('[reCAPTCHA] Not ready', loadError)
            onError?.('not_ready')
            return
          }
        }

        // execute() pode travar (timeout no cliente). Google recomenda retry do execute().
        // Tentamos até MAX_ATTEMPTS antes de desistir; cada tentativa corre contra um timeout.
        let token: string | undefined
        let lastError: unknown
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          let timeoutId: ReturnType<typeof setTimeout> | undefined
          try {
            token = await Promise.race([
              recaptchaApi.execute(settings.RECAPTCHA_SITE_KEY_V3, { action }),
              new Promise<never>((_, reject) => {
                timeoutId = setTimeout(
                  () => reject(new Error(TIMEOUT_ERROR_MESSAGE)),
                  EXECUTION_TIMEOUT_MS
                )
              })
            ])
            break
          } catch (captchaError) {
            lastError = captchaError
            console.error(
              '[reCAPTCHA] Token execution failed (attempt %d/%d)',
              attempt,
              MAX_ATTEMPTS,
              captchaError
            )
            if (attempt < MAX_ATTEMPTS) {
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS))
            }
          } finally {
            clearTimeout(timeoutId)
          }
        }

        // Todas as tentativas de gerar o token falharam → erro de reCAPTCHA (FASE 1).
        if (token === undefined) {
          const timedOut = lastError instanceof Error && lastError.message === TIMEOUT_ERROR_MESSAGE
          errorMonitor.captureException(lastError, {
            tags: { feature: 'recaptcha', action },
            extra: { attempts: MAX_ATTEMPTS, reason: timedOut ? 'timeout' : 'token_failed' },
            level: 'warning'
          })
          onError?.(timedOut ? 'timeout' : 'token_failed')
          return
        }

        setRecaptchaToken(token)
        setRecaptchaSiteKey(settings.RECAPTCHA_SITE_KEY_V3)

        // Marca que token foi obtido com sucesso
        // Se falha daqui em diante, é erro de negócio
        tokenObtained = true

        // FASE 2: Executar callback (login, change password, etc)
        // Dentro do try para manter o lock ativo durante o callback e evitar execuções
        // concorrentes. O retry acima é local ao for; erros do callback não o disparam,
        // apenas propagam para o .catch do chamador.
        await callback()
      } catch (error) {
        // Se erro em FASE 1 (token não gerado) → erro reCAPTCHA
        if (!tokenObtained) {
          console.error('[reCAPTCHA] Token execution failed', error)
          errorMonitor.captureException(error)
          onError?.('token_failed')
        }
        // Se erro em FASE 2 (callback) → erro de negócio, propaga
        else {
          throw error
        }
      } finally {
        isExecutingRef.current = false
      }
    },
    [api]
  )

  // Hoje `api` é o único state e `executeRecaptcha` depende dele, então o memo nunca
  // segura um render que o objeto literal deixaria passar. Fica como proteção para o
  // dia em que o provider ganhar state próprio: sem ele, qualquer setState novo
  // invalidaria o context e re-renderizaria todos os consumidores em silêncio.
  const contextValue = useMemo(() => ({ executeRecaptcha }), [executeRecaptcha])

  return <RecaptchaContext.Provider value={contextValue}>{children}</RecaptchaContext.Provider>
}
